// Internal modules
import {
  containsRightOfWithdrawalInUserAgreement,
  features,
  getLocale,
  isEmeaLiteCountry,
  isEmeaPasswordlessWithoutCookiesCountry,
  logger,
} from '@/lib';

import { RIGHT_OF_WITHDRAWAL_TERM_KEY } from './constants';
import { CountryCodes } from '@/generated/graphql';

export const getOnboardTermsKey = (type: 'guest' | 'signup'): string => {
  const isSignup = type === 'signup';
  if (features.isActive('hasPrivacyStatementTerms') && isSignup) {
    return 'terms.userAgreementLink';
  }

  // For some countries, according to FEATURE_SIGNUP_RIGHT_OF_WITHDRAW
  // we need to show signup terms with Right of Withdrawal
  if (containsRightOfWithdrawalInUserAgreement(type)) {
    return RIGHT_OF_WITHDRAWAL_TERM_KEY;
  }

  if (
    !isSignup &&
    features.isActive('shouldShowDomesticUserAgreementAcceptance')
  ) {
    return 'simplifiedSectionHeaders.guest.domesticTermsWithLinks';
  }

  const key = isEmeaLiteCountry() ? `${type}EmeaLite` : type;
  return `terms.${key}`;
};

interface GetOnboardTermsStreamlineKeyParams {
  type: 'guest' | 'signup';
  isPasswordless?: boolean;
}

/**
  getOnboardTermsStreamlineKey function returns a content key
  for a streamline experience. And it depends on the type of
  the flow (signup/guest/isPasswordless/emeaLight country group)
*/
export const getOnboardTermsStreamlineKey = ({
  type,
  isPasswordless,
}: GetOnboardTermsStreamlineKeyParams): string => {
  const isSignup = type === 'signup';
  if (features.isActive('hasPrivacyStatementTerms') && isSignup) {
    return 'terms.userAgreementLink';
  }

  // For some countries, according to FEATURE_SIGNUP_RIGHT_OF_WITHDRAW
  // we need to show signup terms with Right of Withdrawal
  if (containsRightOfWithdrawalInUserAgreement(type)) {
    return RIGHT_OF_WITHDRAWAL_TERM_KEY;
  }

  if (features.isActive('shouldShowILPSLArticle2Content') && !isSignup) {
    return 'simplifiedSectionHeaders.guest.termsWithLinksArticle2';
  }

  if (
    !isSignup &&
    features.isActive('shouldShowDomesticUserAgreementAcceptance')
  ) {
    return 'simplifiedSectionHeaders.guest.domesticTermsWithLinks';
  }

  if (isPasswordless && isEmeaPasswordlessWithoutCookiesCountry()) {
    const termsType = isSignup ? 'termsAndConditions' : 'termsWithLinks';
    return `simplifiedSectionHeaders.${type}.${termsType}`;
  }

  const emeaKeySignup =
    isSignup && isPasswordless ? 'termsWithLinks' : 'termsAndConditions';

  const emeaKey = isSignup ? emeaKeySignup : 'termsWithLinks';

  const streamlineKey = isEmeaLiteCountry() ? `${type}EmeaLite` : emeaKey;

  const { country } = getLocale();
  const hasShorterSignupTerms = country === CountryCodes.Us;

  if (isSignup && hasShorterSignupTerms) {
    return 'simplifiedSectionHeaders.signup.termsWithLinksV2';
  }

  if (isSignup && isPasswordless && features.isActive('isWebLLS')) {
    return 'simplifiedSectionHeaders.streamline.webllsterms';
  }

  return `simplifiedSectionHeaders.${type}.${streamlineKey}`;
};

export const logTermClick = (linkName: string): void => {
  const snakedLinkName = linkName.replace(
    /[A-Z]/g,
    match => `_${match.toLowerCase()}`,
  );
  const eventName = `prepare_${snakedLinkName}_terms`;
  logger.fpti(`weasley_${eventName}`, {
    state_name: 'signup',
    transition_name: eventName,
  });
};

export const logLinkClick = (linkName: string, path: string): void => {
  logger.info('terms_link_click', { linkName, path });
};

export const getFeeInformationServiceDescriptionKey = ({
  isoCode,
}: {
  isoCode: CountryCodes;
}): string | null => {
  switch (isoCode) {
    case CountryCodes.Pl:
    case CountryCodes.Es:
    case CountryCodes.De:
      return 'compliance.feeInformation';
    case CountryCodes.Lt:
      return 'compliance.commissionInformation';
    case CountryCodes.At:
      return 'compliance.serviceDescription';
    default:
      return null;
  }
};
